/**
 * Page Campagnes — logique de présentation pure (sans React) : statut
 * effectif, décoration d'une campagne pour les cartes et le tableau,
 * filtres, tri, pagination, indicateurs. Testée à part de la page.
 */
import { ancienneteLabel } from "@/crm/anciennete";

export const STATUS_META = {
  running: { label: "En cours", bg: "#fdf3e7", fg: "#8a6116", rail: "#f0b849", bar: "#005bd3" },
  done: { label: "Terminée", bg: "#e7f5f1", fg: "#005c48", rail: "#008060", bar: "#008060" },
  draft: { label: "Non lancée", bg: "#f2f4f7", fg: "#475569", rail: "#e3e3e3", bar: "#005bd3" },
  error: { label: "Erreur", bg: "#fdecea", fg: "#b42318", rail: "#d72c0d", bar: "#d72c0d" },
};

export const STATUS_TABS = [
  { key: "all", label: "Toutes" },
  { key: "running", label: "En cours" },
  { key: "done", label: "Terminées" },
  { key: "draft", label: "Non lancées" },
  { key: "error", label: "Erreur" },
  { key: "archived", label: "Archivées" },
];

/** Jours d'archive avant suppression automatique (aligné sur le backend,
 *  `crm/campaigns_lifecycle.RETENTION_DAYS`). Le serveur reste la référence :
 *  cette valeur ne sert qu'aux textes affichés quand il n'a rien renvoyé. */
export const RETENTION_DAYS = 90;

/** Compte à rebours d'une archive, en clair. */
export function purgeLabel(days) {
  if (days == null) return "";
  if (days <= 0) return "Suppression imminente";
  if (days === 1) return "Suppression demain";
  return `Suppression dans ${days} jours`;
}

export const PER_PAGE_OPTIONS = [8, 16, 32];

/** Statut effectif : une extraction en cours (RUNNING_JOBS côté serveur) prime
 *  sur le statut stocké, et un statut inconnu est traité comme un brouillon. */
export function statusOf(c) {
  if (c.live?.status === "running" || c.status === "running") return "running";
  return STATUS_META[c.status] ? c.status : "draft";
}

/** « il y a 2 min », « aujourd'hui, 18:04 », « hier, 18:04 », « 27/08, 09:12 ». */
export function fmtLastRun(iso, now = Date.now()) {
  if (!iso) return "Jamais";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Jamais";
  const m = Math.floor((now - d.getTime()) / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return `aujourd'hui, ${time}`;
  if (d.toDateString() === yesterday.toDateString()) return `hier, ${time}`;
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}, ${time}`;
}

const plural = (n, one, many) => (n > 1 ? many : one);

/** Une campagne prête à afficher : statut effectif, compteur, pourcentage,
 *  chips de critères, méta courte, message d'erreur. */
export function decorate(c) {
  const statusKey = statusOf(c);
  // L'archive est un état À PART du statut d'extraction : une campagne
  // archivée reste « Terminée », elle est simplement sortie de la liste de
  // travail. Deux dimensions, deux champs.
  const archived = !!c.archived_at;
  const daysBeforePurge = archived && Number.isFinite(c.days_before_purge) ? c.days_before_purge : null;
  const objectif = Number(c.objectif) || 0;
  const count = Number(c.companies_count) || 0;
  const pct = objectif > 0 ? Math.min(100, Math.round((count / objectif) * 100)) : 0;
  const cpCount = Array.isArray(c.codes_postaux) ? c.codes_postaux.length : 0;
  // La source Google se lit d'abord (ses critères n'ont pas le même sens) ;
  // le registre, source historique, reste implicite.
  const google = c.source === "google_places";
  const gType = c.google_type_label || c.google_type;
  const chips = [
    google && { kind: "source", label: "Google Places" },
    google && gType && { kind: "gtype", label: gType },
    c.departement && { kind: "dept", label: `Dépt ${c.departement}` },
    !google && c.naf && { kind: "naf", label: `NAF ${c.naf}` },
    cpCount ? { kind: "cp", label: `${cpCount} ${plural(cpCount, "code postal", "codes postaux")}` } : null,
    c.query && { kind: "query", label: `« ${c.query} »` },
    !google && c.anciennete_max_jours && { kind: "age", label: `Créées ${ancienneteLabel(c.anciennete_max_jours)}` },
    !google && c.etat_administratif === "C" && { kind: "closed", label: "Cessées" },
  ].filter(Boolean);
  const meta = [
    google && "Google",
    google && gType,
    c.departement && `Dépt ${c.departement}`,
    !google && c.naf && `NAF ${c.naf}`,
    cpCount ? `${cpCount} CP` : null,
  ].filter(Boolean).join(" · ");
  return {
    ...c,
    statusKey,
    archived,
    archivedAt: c.archived_at || null,
    daysBeforePurge,
    purgeText: purgeLabel(daysBeforePurge),
    running: statusKey === "running",
    count,
    objectif,
    pct,
    chips,
    meta,
    current: c.live?.current || "",
    lastRunAt: c.last_run_at || null,
    error: statusKey === "error" ? (c.last_error || "Échec de la dernière extraction.") : "",
  };
}

export function computeKpis(items) {
  const done = items.filter((c) => c.statusKey === "done");
  const objectifRate = done.length
    ? Math.round(done.reduce((n, c) => n + Math.min(100, c.pct), 0) / done.length)
    : 0;
  return {
    campaigns: items.length,
    imported: items.reduce((n, c) => n + c.count, 0),
    running: items.filter((c) => c.running).length,
    objectifRate,
  };
}

export function countByStatus(items) {
  const actives = items.filter((c) => !c.archived);
  const counts = { all: actives.length, running: 0, done: 0, draft: 0, error: 0,
    archived: items.length - actives.length };
  for (const c of actives) counts[c.statusKey] = (counts[c.statusKey] || 0) + 1;
  return counts;
}

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

/** Recherche (nom + critères) et filtre de statut se combinent.
 *
 *  L'archive ne se mélange jamais au reste : « Toutes » veut dire toutes les
 *  campagnes actives, sinon archiver ne servirait à rien. On ne voit les
 *  archivées que dans leur propre onglet. */
export function filterCampaigns(items, { q = "", status = "all" } = {}) {
  const needle = norm(q.trim());
  const visible = (c) => (status === "archived"
    ? c.archived
    : !c.archived && (status === "all" || c.statusKey === status));
  return items.filter((c) => visible(c)
    && (!needle || norm(`${c.name} ${c.meta} ${c.query || ""} ${c.section_activite || ""}`).includes(needle)));
}

const STATUS_ORDER = { running: 0, error: 1, draft: 2, done: 3 };

export function sortCampaigns(items, key = "name", dir = "asc") {
  const sign = dir === "desc" ? -1 : 1;
  const cmp = {
    name: (a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr"),
    status: (a, b) => STATUS_ORDER[a.statusKey] - STATUS_ORDER[b.statusKey],
    count: (a, b) => a.count - b.count,
    // Jamais exécutée = en fin de liste quel que soit le sens.
    lastRun: (a, b) => {
      if (!a.lastRunAt && !b.lastRunAt) return 0;
      if (!a.lastRunAt) return 1 * sign;
      if (!b.lastRunAt) return -1 * sign;
      return String(a.lastRunAt).localeCompare(String(b.lastRunAt));
    },
  }[key] || (() => 0);
  return items.slice().sort((a, b) => cmp(a, b) * sign || String(a.name || "").localeCompare(String(b.name || ""), "fr"));
}

export function paginate(items, page = 1, perPage = 8) {
  const pageCount = Math.max(1, Math.ceil(items.length / perPage));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * perPage;
  return { page: current, pageCount, start, end: Math.min(start + perPage, items.length), items: items.slice(start, start + perPage) };
}
